import { colors } from '@/theme'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Box, Button, Collapse, IconButton, Paper, Typography } from '@mui/material'
import { useState } from 'react'

interface CollapsibleSectionProps {
  id?: string
  title: string
  children?: React.ReactNode
  defaultOpen?: boolean
  actionButton?: {
    label: string
    onClick: () => void
  }
}

export default function CollapsibleSection({
  id,
  title,
  children,
  defaultOpen = true,
  actionButton
}: CollapsibleSectionProps) {
  const hasContent = !!children
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <Box id={id}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: `1px solid ${colors.border.default}`,
          bgcolor: colors.background.paper,
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            ...(hasContent && {
              cursor: 'pointer',
              '&:hover': { bgcolor: colors.background.surface }
            })
          }}
          onClick={hasContent ? () => setIsOpen(!isOpen) : undefined}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              sx={{
                color: colors.text.dark,
                fontSize: '16px',
                fontWeight: 600,
                lineHeight: 1.5
              }}
            >
              {title}
            </Typography>
            {actionButton && (
              <Button
                variant='secondary'
                size='small'
                onClick={e => {
                  e.stopPropagation()
                  actionButton.onClick()
                }}
              >
                {actionButton.label}
              </Button>
            )}
          </Box>
          {hasContent && <IconButton size='small'>{isOpen ? <ExpandLess /> : <ExpandMore />}</IconButton>}
        </Box>

        {/* Collapsible Content */}
        {hasContent && (
          <Collapse in={isOpen}>
            <Box sx={{ px: 3, pb: 3 }}>{children}</Box>
          </Collapse>
        )}
      </Paper>
    </Box>
  )
}
